import React from "react";
import Nullable from "@data-force/app/interfaces/nullable";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import "./Footer.scss";
import {
    Button, Layout, Modal, Progress, Dropdown, Menu,
} from "antd";
import {DownOutlined} from "@ant-design/icons";
import classNames from "classnames";
import {
    checkTranslation,
    DisplayType,
    EditorType,
    ElementsValues,
    ElementsValuesType,
    formatString,
    getElementsValues,
    RefElementsMap,
} from "@data-force/app";
import StoreState from "../../interfaces/store/storeState";
import {COMPLETE_EVENT, REJECT_EVENT} from "../../utils/constants";
import {
    batchUploadsAreValid,
    calculateIntakeCompletionPercentage,
    calculateNumberOfTasks,
    calculateStatusCount,
    countValidTasks,
    getHistoryParams,
    initUploadsData,
    isEditableTask,
    isIntaker,
    isMetadataValid,
    isTaskMetadataValid,
    mergeUploadBuckets,
    isValidByBuckets,
    getRejectionReason,
    isPhaseExpired,
} from "../../utils/common";
import HistoryParams from "../../interfaces/historyParams";
import {WorkflowDefinition, WorkflowDefinitionPhases} from "../../interfaces/store/actions/payload/workflowDefinitions";
import {completeIntake} from "../../store/actions/task";
import {CompleteIntakeAction} from "../../interfaces/store/actions/task";
import {
    CompleteIntakePayload,
    CompleteType,
    Status,
    StatusStats,
    Task,
    TaskCompletionData,
    TaskEntity,
} from "../../interfaces/store/actions/payload/task";
import {BulkConfirmationPayload, ContentType, FileSelectionType} from "../../interfaces/store/actions/payload/layout";
import {setBulkConfirmationDialog} from "../../store/actions/layout";
import {SetBulkConfirmationDialogAction} from "../../interfaces/store/actions/layout";
import {GetFormErrorsPayload, TaskStatusCount} from "../../interfaces/store/actions/payload/job";
import {UploadsBucket} from "../../interfaces/store/reducers/uploadFilesState";
import {UploadsData} from "../../interfaces/store/actions/payload/fileValidation";
import {getEnableBulkCompletePhaseAction} from "../../store/actions/project";
import {GetEnableBulkCompletePhaseAction} from "../../interfaces/store/actions/project";

interface TasksStatusCount {
    ready: number
    inProgress: number
    completed: number
    notStarted: number
    reopened: number
}

interface StateProps {
    invalidNumberOfFiles: boolean
    validMetadata: boolean
    translate: TranslateFunction
    workflowDefinitions: WorkflowDefinition[]
    workflowDefinitionId: string
    statusStats: Nullable<StatusStats>
    activeTask: Nullable<Task>
    tasks: TaskEntity[]
    numberUploadFiles: number
    failedDialogVisible: boolean
    failedDialogEvent: string
    failedFileUploadPaths: string[]
    displayType: DisplayType
    contentType: ContentType
    intakeCompletionPercentage: number
    isTaskReopened: boolean
    bulkConfirmEvent: string
    bulkConfirmVisible: boolean
    failedActionType: CompleteType
    validTaskMetadata: boolean
    activeTaskFileSelected: boolean
    fileSelectionType: Nullable<FileSelectionType>
    validByBuckets: boolean
    enableBulkCompletePhaseAction: boolean
    isRejectedTask: boolean
    isCompletedTask: boolean
    phaseExpired: boolean
}

interface DispatchProps {
    completeIntake(event: CompleteIntakePayload): CompleteIntakeAction

    setBulkConfirmationDialog(event: BulkConfirmationPayload): SetBulkConfirmationDialogAction

    getEnableBulkCompletePhaseAction(): GetEnableBulkCompletePhaseAction
}

type Props = StateProps & DispatchProps;

class Footer extends React.Component<Props> {
    private static readonly translationPath: string = "components.Footer.Footer";

    constructor(props: Props) {
        super(props);
        this.isJobEditable = this.isJobEditable.bind(this);
        this.getAvailableEvents = this.getAvailableEvents.bind(this);
        this.generateActionButtons = this.generateActionButtons.bind(this);
        this.getPhaseEvents = this.getPhaseEvents.bind(this);
        this.generateFailedUploadDialog = this.generateFailedUploadDialog.bind(this);
        this.isCompleteAllDisabled = this.isCompleteAllDisabled.bind(this);
        this.processCompleteIntake = this.processCompleteIntake.bind(this);
        this.generateBulkConfirmDialog = this.generateBulkConfirmDialog.bind(this);
        this.handleTaskCompletion = this.handleTaskCompletion.bind(this);
        this.isCompleteTaskDisabled = this.isCompleteTaskDisabled.bind(this);
    }

    componentDidMount(): void {
        this.props.getEnableBulkCompletePhaseAction();
    }

    /**
     * Retrieves list of available phase events
     *
     * @param {Nullable<WorkflowDefinitionPhases>} phase
     * @return {string[]} - availableEvents
     */
    getPhaseEvents(phase: Nullable<WorkflowDefinitionPhases>): string[] {
        let availableEvents: string[] = [];
        if (phase) {
            const {availableEvents: phaseAvailableEvents, parent, name}: WorkflowDefinitionPhases = phase;
            const parallelPhases: boolean = !!parent;
            if (parallelPhases) {
                availableEvents = phaseAvailableEvents?.filter(
                    (event: string): boolean => event.includes(`.${name}`)
                ) || [];
            } else {
                availableEvents = phaseAvailableEvents || [];
            }
        }

        return availableEvents;
    }

    /**
     * Retrieves list of available events
     *
     * @return {string[]} - availableEvents
     */
    getAvailableEvents(): string[] {
        let availableEvents: string[] = [];
        if (this.isJobEditable()) {
            const {workflowDefinitions, workflowDefinitionId}: Props = this.props;
            const workflowDefinition: Nullable<WorkflowDefinition> = workflowDefinitions.find(
                (workflow: WorkflowDefinition): boolean => workflowDefinitionId === workflow.id,
            );
            if (workflowDefinition) {
                const historyParams: Nullable<HistoryParams> = getHistoryParams();
                if (historyParams) {
                    const {phase: currentPhase}: HistoryParams = historyParams;
                    const phase: Nullable<WorkflowDefinitionPhases> = workflowDefinition.phases.find(
                        (p: WorkflowDefinitionPhases): boolean => p.name === currentPhase,
                    );
                    availableEvents = this.getPhaseEvents(phase);
                }
            }
        }

        return availableEvents;
    }

    /**
     * Sets visibility of "Bulk Complete" dialog
     *
     * @param {boolean} visible
     */
    setBulkConfirmationDialogVisibility(visible: boolean): void {
        this.props.setBulkConfirmationDialog({event: "", visible});
    }

    /**
     * Triggers Intake completion, both single or bulk.
     *
     * @param {TaskCompletionData} taskCompletionData
     */
    processCompleteIntake(taskCompletionData: TaskCompletionData): void {
        const {event, completeType, isRetry}: TaskCompletionData = taskCompletionData;
        this.props.completeIntake({
            event,
            isRetry,
            taskList: [],
            completeType,
        });
    }

    /**
     * Returns true if current job is editable
     *
     * @return {boolean}
     */
    isJobEditable(): boolean {
        const {statusStats}: Props = this.props;
        const tasksStatusCount: TasksStatusCount = calculateStatusCount(statusStats);
        const {
            ready, inProgress, notStarted, reopened,
        }: TasksStatusCount = tasksStatusCount;
        return (notStarted === 0 && (ready > 0 || inProgress > 0 || reopened > 0));
    }

    /**
     * Determines if bulk complete action is available.
     */
    isCompleteAllDisabled(): boolean {
        const {
            invalidNumberOfFiles, validMetadata, validByBuckets, enableBulkCompletePhaseAction,
        }: Props = this.props;

        return (invalidNumberOfFiles || !validMetadata || !validByBuckets || !enableBulkCompletePhaseAction);
    }

    /**
     * Determines if single complete action is available.
     */
    isCompleteTaskDisabled(): boolean {
        const {activeTaskFileSelected, validTaskMetadata}: Props = this.props;
        return (!activeTaskFileSelected || !validTaskMetadata);
    }

    /**
     * Closes confirmation dialog and triggers Intake completion.
     *
     * @param {TaskCompletionData} taskCompletionData
     */
    processTaskCompletion(taskCompletionData: TaskCompletionData): void {
        const {event}: TaskCompletionData = taskCompletionData;
        this.props.setBulkConfirmationDialog({event, visible: false});
        this.processCompleteIntake(taskCompletionData);
    }

    /**
     * Handles confirmation dialogs on complete
     *
     * @param {TaskCompletionData} taskCompletionData
     */
    handleTaskCompletion(taskCompletionData: TaskCompletionData): void {
        const {event, completeType}: TaskCompletionData = taskCompletionData;
        if (completeType === CompleteType.BULK) {
            this.props.setBulkConfirmationDialog({event, visible: true});
        } else {
            this.processTaskCompletion(taskCompletionData);
        }
    }

    /**
     * Renders "Complete Intake" confirmation dialog.
     *
     * @param {string} event
     * @param {boolean} visible
     * @param {Function} cancelFn
     * @param {Function} confirmFn
     */
    generateBulkConfirmDialog(
        event: string,
        visible: boolean,
        cancelFn: Function,
        confirmFn: Function,
    ): React.ReactNode {
        const {translate}: Props = this.props;
        const {translationPath}: typeof Footer = Footer;
        const taskCompletionData: TaskCompletionData = {
            completeType: CompleteType.BULK,
            isRetry: false,
            event,
        };
        return (
            <Modal
                title={<Translate id={`${translationPath}.confirmDialog.${event}.title.text`}/>}
                visible={visible}
                className="df-bulk-confirm-dialog"
                closable={false}
                maskClosable={false}
                footer={[
                    <Button
                        key="cancel"
                        type="primary"
                        className="df-cancel-button"
                        onClick={cancelFn.bind(this, false)}
                    >
                        <Translate id={`${translationPath}.confirmDialog.cancelBtn.text`}/>
                    </Button>,
                    <Button
                        key="confirm"
                        className="df-confirm-task-validation"
                        onClick={confirmFn.bind(this, taskCompletionData)}
                    >
                        {checkTranslation(`${translationPath}.${event}-BULK.text`, event, translate)}
                    </Button>,
                ]}
            >
                <Translate id={`${translationPath}.confirmDialog.${event}.message.text`}/>
            </Modal>
        );
    }

    /**
     * Generates buttons for all events (actions) defined for Collection phase.
     *
     * @return {React.ReactNode[]}
     */
    generateActionButtons(): React.ReactNode[] {
        const {
            translate, displayType, activeTask, phaseExpired,
        }: Props = this.props;
        const {translationPath}: typeof Footer = Footer;
        const availableEvents: string[] = this.getAvailableEvents();

        return availableEvents?.map((availableEvent: string): React.ReactNode => {
            const event: string = availableEvent.split(".")[0].toUpperCase();
            const buttonClass: string = classNames({
                "df-complete-btn": event === COMPLETE_EVENT,
                "df-btn-order-99": event === COMPLETE_EVENT,
                "df-reject-btn": event === REJECT_EVENT,
                "df-margin-16": event !== COMPLETE_EVENT,
                "df-action-btn": ![COMPLETE_EVENT, REJECT_EVENT].includes(event),
            });
            const disabledBulk: boolean = (event === COMPLETE_EVENT && this.isCompleteAllDisabled());
            const disabledSingle: boolean = (event === COMPLETE_EVENT && this.isCompleteTaskDisabled());
            const standardActionVisible: boolean = !!activeTask && isIntaker(displayType);

            let actionButton: React.ReactNode;
            if (standardActionVisible) {
                actionButton = (
                    <Dropdown
                        key={`df-${event}-dropdown`}
                        trigger={["click"]}
                        placement="top"
                        disabled={(disabledSingle && disabledBulk) || phaseExpired}
                        overlay={(
                            <Menu className="df-footer-action-menu">
                                <Menu.Item
                                    key={event}
                                    onClick={this.handleTaskCompletion.bind(this, {
                                        event: availableEvent,
                                        completeType: CompleteType.SINGLE,
                                        isRetry: false,
                                    })}
                                    disabled={disabledSingle}
                                >
                                    {checkTranslation(`${translationPath}.${event}-SINGLE.text`, event, translate)}
                                </Menu.Item>
                                <Menu.Item
                                    key={`${event}-ALL`}
                                    onClick={this.handleTaskCompletion.bind(this, {
                                        event: availableEvent,
                                        completeType: CompleteType.BULK,
                                        isRetry: false,
                                    })}
                                    disabled={disabledBulk}
                                >
                                    {checkTranslation(`${translationPath}.${event}-BULK.text`, event, translate)}
                                </Menu.Item>
                            </Menu>
                        )}
                    >
                        <Button
                            className={buttonClass}
                            type="primary"
                            disabled={disabledSingle && disabledBulk}
                        >
                            {checkTranslation(`${translationPath}.${event}.text`, event, translate)}
                            <DownOutlined/>
                        </Button>
                    </Dropdown>
                );
            } else {
                actionButton = (
                    <Button
                        onClick={this.processCompleteIntake.bind(this, {
                            event: availableEvent,
                            isRetry: false,
                            completeType: CompleteType.BULK,
                        })}
                        className={buttonClass}
                        type="primary"
                        disabled={disabledBulk && phaseExpired}
                        key={event}
                    >
                        {checkTranslation(`${translationPath}.${event}.text`, event, translate)}
                    </Button>
                );
            }
            return actionButton;
        });
    }

    /**
     * Renders "failed uploads" dialog
     */
    generateFailedUploadDialog(): React.ReactNode {
        const {
            failedDialogVisible,
            translate,
            failedFileUploadPaths,
            numberUploadFiles,
            failedDialogEvent,
            failedActionType,
        }: Props = this.props;
        const {translationPath}: typeof Footer = Footer;
        const numberOfFailedUploads: number = failedFileUploadPaths.length;
        const crudMessage: string = translate(`${translationPath}.failedFileUpload.text`) as string;
        const wasConnector: string = translate(
            `${translationPath}.${numberOfFailedUploads === 1 ? "was" : "were"}.text`,
        ) as string;
        const modalMessage: string = formatString(
            crudMessage,
            numberOfFailedUploads.toString(),
            numberUploadFiles.toString(),
            wasConnector,
        );
        return (
            <Modal
                title={<Translate id={`${translationPath}.intakeFailed.text`}/>}
                visible={failedDialogVisible}
                className="df-failed-upload-dialog"
                closable={false}
                maskClosable={false}
                footer={[
                    <Button
                        key="confirm"
                        type="primary"
                        onClick={this.processCompleteIntake.bind(this, {
                            event: failedDialogEvent,
                            isRetry: true,
                            completeType: failedActionType,
                        })}
                    >
                        <Translate id={`${translationPath}.retryIntake.text`}/>
                    </Button>,
                ]}
            >
                <div className="df-failed-upload-message">{modalMessage}</div>
            </Modal>
        );
    }

    render() {
        const {
            failedDialogVisible,
            intakeCompletionPercentage,
            displayType,
            contentType,
            bulkConfirmEvent,
            bulkConfirmVisible,
            fileSelectionType,
        }: Props = this.props;
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        const progressVisible: boolean = historyParams && isIntaker(displayType) && contentType === ContentType.TASKS;
        const actionsVisible: boolean = displayType === DisplayType.DICTATION
            || fileSelectionType === FileSelectionType.SINGLE;

        return (
            <Layout.Footer className="df-footer">
                {progressVisible && (
                    <div className="df-progress-box">
                        <div className="df-progress-box-inner">
                            <h2>
                                <Translate id={`${Footer.translationPath}.progress.text`}/>
                            </h2>
                            <span className="df-progress-percentage">{`${intakeCompletionPercentage}%`}</span>
                            <Progress
                                showInfo={false}
                                percent={intakeCompletionPercentage}
                            />
                        </div>
                    </div>
                )}
                <div className="df-complete-intake">
                    <div className="df-btn-wrap">
                        {actionsVisible && this.generateActionButtons()}
                    </div>
                </div>
                {failedDialogVisible && this.generateFailedUploadDialog()}
                {
                    this.generateBulkConfirmDialog(
                        bulkConfirmEvent,
                        bulkConfirmVisible,
                        this.setBulkConfirmationDialogVisibility,
                        this.processTaskCompletion,
                    )
                }
            </Layout.Footer>
        );
    }
}

function mapStateToProps(
    {
        job: {
            jobPhase,
            jobDetails,
            failedUploadDialog: {
                visible,
                event,
                completeType: failedActionType,
            },
            failedFileUploadPaths,
            tasks,
            activeTask,
        },
        project: {
            projectDetails,
            workflowDefinitions,
            enableBulkCompletePhaseAction,
        },
        schema: {
            elementsValuesMap,
            parsedSchemas,
        },
        localize,
        layout: {
            content: {
                contentType,
                fileSelectionType,
            },
            footer: {
                dialog: {
                    bulkConfirmationDialog: {
                        event: bulkConfirmEvent,
                        visible: bulkConfirmVisible,
                    },
                },
            },
        },
        uploadFiles,
    }: StoreState,
): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${projectDetails?.schemaId}_${historyParams?.phase}`;
    const {completed, rejected}: TaskStatusCount = calculateStatusCount(jobPhase?.phaseStats);
    const numberOfRequiredFiles: number = calculateNumberOfTasks(jobPhase?.phaseStats);
    const uploadBucket: UploadsBucket = mergeUploadBuckets(uploadFiles.selectedFiles);
    const numberUploadFiles: number = Object.values(uploadBucket).length;
    const editor: EditorType = parsedSchemas[schemaKey] as EditorType;
    const translate: TranslateFunction = getTranslate(localize);
    const formErrorsPayload: GetFormErrorsPayload = {
        elementsValuesMap,
        schemaKey,
        editor,
        translate,
        jobId: historyParams?.jobId,
        tasks,
    };
    const validMetadata: boolean = isMetadataValid(formErrorsPayload);
    const validTaskMetadata: boolean = isTaskMetadataValid(formErrorsPayload, activeTask);
    const activeTaskFileSelected: boolean = activeTask?.id ? !!uploadBucket[activeTask.id] : false;
    const validTaskCount: number = countValidTasks({formErrorsPayload, uploadBucket});
    const collectionRefElements: RefElementsMap = editor?.refElements || {};
    const elementsValues: ElementsValues = getElementsValues({
        metadataId: historyParams?.jobId,
        elementsValuesType: ElementsValuesType.JOB,
        schemaKey,
        elementsValuesMap,
    });
    const uploadsData: UploadsData = initUploadsData({
        editor,
        collectionRefElements,
        elementsValues,
        translate,
        translationPath: "components.FilePicker.FilePicker",
        tasks: tasks || [],
        elementsValuesMap,
        schemaKey,
        uploadBucket,
    });
    const validBatchUploadsConfig: boolean = editor?.batchUploads && editor?.uploadReference
        ? batchUploadsAreValid(editor.batchUploads, editor.uploadReference)
        : false;

    const activeTaskEntity: Nullable<TaskEntity> = tasks.find(
        ({payload}: TaskEntity): boolean => payload === activeTask?.id,
    );
    const rejectionReason: Nullable<string> = getRejectionReason(editor, elementsValues);
    const isRejectedTask: boolean = !!activeTaskEntity
        && (activeTaskEntity.status === Status.REJECTED
            || (activeTaskEntity.status === Status.COMPLETED && !!rejectionReason));
    const isCompletedTask: boolean = !!activeTaskEntity
        && (activeTaskEntity.status === Status.COMPLETED && !rejectionReason);

    return {
        invalidNumberOfFiles: numberUploadFiles !== numberOfRequiredFiles || numberUploadFiles === 0,
        validMetadata,
        translate,
        workflowDefinitions,
        workflowDefinitionId: jobDetails?.workflowDefinitionId || "",
        statusStats: jobPhase?.phaseStats,
        activeTask,
        tasks,
        numberUploadFiles,
        failedDialogVisible: visible,
        failedDialogEvent: event,
        failedFileUploadPaths,
        displayType: editor?.layout || DisplayType.INTAKER_WIZARD,
        contentType,
        intakeCompletionPercentage: calculateIntakeCompletionPercentage({
            totalTasks: tasks.length,
            validTaskCount,
            rejected,
            completed,
        }),
        isTaskReopened: isEditableTask(activeTask, tasks),
        bulkConfirmEvent,
        bulkConfirmVisible,
        failedActionType,
        validTaskMetadata,
        activeTaskFileSelected,
        fileSelectionType,
        validByBuckets: isValidByBuckets({
            uploadsData,
            uploadFiles,
            validBatchUploadsConfig,
        }),
        enableBulkCompletePhaseAction,
        isRejectedTask,
        isCompletedTask,
        phaseExpired: isPhaseExpired(jobPhase.accessStatus),
    };
}

const mapDispatchToProps: DispatchProps = {
    completeIntake,
    setBulkConfirmationDialog,
    getEnableBulkCompletePhaseAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
